import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AlarmClock, RefreshCw } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Button,
  Card,
  StatCard,
  StatusBadge,
  type StatusTone,
  NotificationCard,
  SearchField,
} from '@afrilink/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import {
  dunningHealthService,
  type DunningHealthRow,
  type DunningNextStep,
} from '@/domains/billing/services/dunningHealthService';
import { formatDate, formatZAR } from '@/shared/utils/format';
import { dunningPolicyMeta } from '@/domains/hosting/dunningPolicy';
import { hostingStateMeta } from '@/domains/hosting/hostingState';

/**
 * Accounts in dunning — the page BRD 2026-06-02 Slice D-6 specified and that
 * was never built. Until 2026-09-10 the answer to "who gets suspended
 * tomorrow?" was a SQL query. The rows come from v_admin_dunning_health,
 * whose predicates mirror billing-cron STEP 2/3 exactly.
 */

const STEP_META: Record<DunningNextStep, { label: string; tone: StatusTone; detail: string }> = {
  grace: { label: 'Enters grace', tone: 'pending', detail: 'Unpaid; moves to grace on the due date (next 04:00 UTC run on or after it).' },
  suspend: { label: 'Suspends', tone: 'overdue', detail: 'In grace; the suspend job is queued when grace_until passes (04:00 UTC run).' },
  exempt: { label: 'Exempt', tone: 'draft', detail: 'In grace, but dunning policy is "never suspend".' },
  held: { label: 'Held', tone: 'info', detail: 'An active payment arrangement holds every ladder step for this customer.' },
  review: { label: 'Review', tone: 'failed', detail: 'Books say suspended but WHM says the account is live, and no job is in flight.' },
  collect: { label: 'Collect', tone: 'overdue', detail: 'Suspended on both sides; debt outstanding. Termination is disarmed platform-wide.' },
  none: { label: '—', tone: 'draft', detail: '' },
};

const WINDOW_OPTIONS = [
  { value: 'all', label: 'Everything in the ladder' },
  { value: '48h', label: 'Next 48 hours' },
  { value: 'review', label: 'Needs review (books ≠ server)' },
] as const;
type WindowFilter = (typeof WINDOW_OPTIONS)[number]['value'];

export default function AdminDunningPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<DunningHealthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [window, setWindow] = useState<WindowFilter>('all');

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      setRows(await dunningHealthService.list());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * SEARCH ONLY — no period control, deliberately.
   *
   * The `48h` / `review` window above is ALREADY this page's temporal control,
   * and it points FORWARD: "what does billing-cron do next, and when". A period
   * filter points backward over an issue date. Two temporal controls facing
   * opposite directions on one screen would look like they should agree, and a
   * user narrowing "last month" would silently drop the account suspending
   * tomorrow — which is the one row this page exists to surface.
   *
   * Same ruling as SalesQuotationsPage, for the same reason.
   */
  const table = useTableFilters<DunningHealthRow>({
    rows,
    searchText: (r) =>
      [
        r.invoice_number,
        r.customer_name ?? '',
        r.cpanel_username,
        r.primary_domain ?? '',
      ].join(' '),
    facets: useMemo(
      () => [
        (r: DunningHealthRow) => {
          if (window === '48h') {
            const cutoff = Date.now() + 48 * 3600_000;
            return !!r.next_step_at && new Date(r.next_step_at).getTime() <= cutoff;
          }
          if (window === 'review') return r.next_step === 'review';
          return true;
        },
      ],
      [window],
    ),
  });

  const filtered = table.rows;

  const stats = useMemo(() => {
    const by = (s: DunningNextStep) => rows.filter((r) => r.next_step === s);
    const owed = rows.reduce((sum, r) => sum + r.total, 0);
    return {
      total: rows.length,
      owed,
      suspending48h: rows.filter(
        (r) => r.next_step === 'suspend' && !!r.next_step_at && new Date(r.next_step_at).getTime() <= Date.now() + 48 * 3600_000,
      ).length,
      review: by('review').length,
      held: by('held').length,
    };
  }, [rows]);

  const columns = useMemo<UniversalTableColumn<DunningHealthRow>[]>(
    () => [
      {
        key: 'customer',
        header: 'Customer',
        render: (r) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{r.customer_name ?? '—'}</p>
            <p className="truncate text-xs text-muted-foreground">
              {r.primary_domain ?? r.cpanel_username} · <code>{r.cpanel_username}</code>
            </p>
          </div>
        ),
      },
      {
        key: 'invoice',
        header: 'Invoice',
        render: (r) => (
          <div>
            <p className="text-sm text-foreground">{r.invoice_number}</p>
            <p className="text-xs text-muted-foreground">{formatZAR(r.total)} · due {formatDate(r.due_date)}</p>
          </div>
        ),
        sortValue: (r) => r.due_date,
      },
      {
        key: 'state',
        header: 'Invoice state',
        render: (r) => <StatusBadge tone={r.lifecycle_state === 'suspended' ? 'failed' : r.lifecycle_state === 'grace' ? 'overdue' : 'pending'}>{r.lifecycle_state}</StatusBadge>,
        width: 'w-32',
      },
      {
        key: 'server',
        header: 'WHM says',
        render: (r) => {
          const m = hostingStateMeta(r.hosting_state);
          return (
            <span title={m.detail}>
              <StatusBadge tone={m.tone}>{m.label}</StatusBadge>
            </span>
          );
        },
        width: 'w-28',
        hideOnMobile: true,
      },
      {
        key: 'next',
        header: 'Next step',
        sortValue: (r) => (r.next_step_at ? new Date(r.next_step_at).getTime() : Number.MAX_SAFE_INTEGER),
        render: (r) => {
          const m = STEP_META[r.next_step];
          return (
            <div className="flex flex-col gap-0.5" title={m.detail}>
              <StatusBadge tone={m.tone}>{m.label}</StatusBadge>
              {r.next_step_at && (
                <span className="text-xs text-muted-foreground">
                  {new Date(r.next_step_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}
                </span>
              )}
            </div>
          );
        },
        width: 'w-44',
      },
      {
        key: 'policy',
        header: 'Policy',
        render: (r) => {
          const m = dunningPolicyMeta(r.dunning_policy);
          return (
            <span title={m.detail}>
              <StatusBadge tone={m.tone}>{m.label}</StatusBadge>
            </span>
          );
        },
        width: 'w-36',
        hideOnMobile: true,
      },
    ],
    [],
  );

  return (
    <UniversalPage
      title="Accounts in dunning"
      description="Every open hosting invoice in the ladder, with the next automated step and when it fires. Same predicates as billing-cron."
      breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Billing', href: '/admin/billing' }, { label: 'Dunning' }]}
      actions={
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing}>
          <RefreshCw className={refreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          Refresh
        </Button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="In the ladder" value={loading ? '…' : String(stats.total)} sub={loading ? '' : `${formatZAR(stats.owed)} outstanding`} />
        <StatCard label="Suspending in 48 h" value={loading ? '…' : String(stats.suspending48h)} tone={stats.suspending48h > 0 ? 'warning' : 'default'} />
        <StatCard label="Needs review" value={loading ? '…' : String(stats.review)} sub="books say suspended, server live" tone={stats.review > 0 ? 'danger' : 'default'} />
        <StatCard label="On arrangement" value={loading ? '…' : String(stats.held)} />
      </div>

      <Card interactive={false} className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="window" className="text-sm text-muted-foreground">Show</label>
          <select
            id="window"
            value={window}
            onChange={(e) => setWindow(e.target.value as WindowFilter)}
            className="h-9 rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span className="ml-auto text-xs text-muted-foreground">
            {loading ? 'Loading…' : `${filtered.length} of ${rows.length}`}
          </span>
        </div>
      </Card>

      {error && !loading && (
        <NotificationCard tone="danger" title="Could not load dunning health" body={error.message} />
      )}

      <div className="mb-3 flex justify-end">
        <SearchField
          value={table.q}
          onValueChange={table.setQ}
          pending={table.isSearching}
          placeholder="Search invoice, customer, domain, cPanel user"
          className="w-full max-w-sm"
        />
      </div>

      <UniversalTable<DunningHealthRow>
        data={filtered}
        caption={table.caption}
        columns={columns}
        getRowKey={(r) => r.invoice_id}
        onRowClick={(r) => navigate(`/admin/customers/${r.customer_id}`)}
        isLoading={loading}
        empty={{
          icon: AlarmClock,
          title: 'Nobody is in dunning',
          description: 'No open hosting invoice is unpaid, in grace, or suspended.',
        }}
      />
    </UniversalPage>
  );
}
